/**
 * Number formatting, shared by every surface that prints a figure.
 *
 * ## Why this is its own module
 *
 * `count()` lives in `chart-library.js` today, which is a chart module. The saved-business
 * picker needs it too, and a list of businesses is not a chart. Importing a formatting helper
 * from the chart library to render a picker row inverts the dependency: a plain page list would
 * be pulling in the whole visualisation module to turn `2074` into `2,074`.
 *
 * So the two rules live here, and `chart-library.js` re-exports `count` from here — one
 * implementation, no duplication, and the direction of the dependency is the sensible one.
 */

/**
 * A review or photo count, as a reader should see it.
 *
 * `2.074 reviews` was live output under a locale whose thousands separator is a period, and
 * reads as *two and a bit*. A business has either 2,074 reviews or it does not, so the
 * separator is forced to a comma regardless of the host.
 *
 * Locale grouping is normally right, and §7.20 established it for the *listing total* — which is
 * Google's own number, rendered by Google in the page's locale, so matching it is what lets a
 * reader compare the two. **Counts on an axis and in a summary line are different**: they are
 * labels, and an axis reading `2.074` beside `14.258` is unreadable to anyone not already
 * reading a European locale. Forced comma, and the deviation is deliberate.
 *
 * @param {number} value
 * @returns {string}
 */
export function count(value) {
  const n = Math.round(value);
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * A star rating, which is never grouped: `4.7`, not `4,700`.
 *
 * @param {number} value
 * @returns {string}
 */
export function ratingText(value) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/**
 * A date, unambiguously.
 *
 * `toLocaleDateString()` produced `09/10/2026` on the recent-audits list, which is 9 October to
 * someone reading day-first and 10 September to someone reading month-first — and this project's
 * screenshots are from Indonesia, where day-first is the convention. Two readers, one string,
 * two dates.
 *
 * `2026-10-09` is the one form nobody has to guess at, and it sorts correctly as a string.
 *
 * @param {string|number|Date} value
 * @returns {string} `YYYY-MM-DD`, or an em dash when unreadable
 */
export function isoDate(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {return '—';}
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * How long ago, in words, beside the exact date.
 *
 * "3 days ago" is what a reader actually wants from a recent list, and it is worthless without
 * the date behind it — which is why both are returned rather than one being chosen.
 *
 * Deliberately coarse. "19 hours ago" implies a precision nobody needs and that gets stranger
 * the longer a page sits open.
 *
 * @param {string|number|Date} value
 * @returns {string}
 */
export function timeAgo(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {return '';}

  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) {return 'just now';}

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {return `${minutes} min ago`;}

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;}

  const days = Math.floor(hours / 24);
  if (days < 30) {return `${days} ${days === 1 ? 'day' : 'days'} ago`;}

  const months = Math.floor(days / 30);
  if (months < 12) {return `${months} ${months === 1 ? 'month' : 'months'} ago`;}

  const years = Math.floor(months / 12);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
}